import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '@/components/common/Footer';
import Header from '@/components/common/Header';
import useChannels from '@/hooks/useChannels';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';
import ChannelButton from './ChannelButton';
import LatestMessage from './LatestMessage';

export interface ChatLandingPageProps {
  onStartChat: () => void;
  onChannelSelect: (channel: string) => void;
  onMinimize: () => void;
}

const ChatLandingPage: React.FC<ChatLandingPageProps> = ({
  onStartChat,
  onChannelSelect,
  onMinimize,
}) => {
  const { t } = useTranslation('common');
  const { branding, channelConfig } = useWidgetConfig();
  const availableChannels = channelConfig.availableChannels;

  const channelLabels = useMemo(
    () => ({
      whatsapp: t('channels.whatsapp'),
      facebook: t('channels.facebook'),
      instagram: t('channels.instagram'),
      wechat: t('channels.wechat'),
      line: t('channels.line'),
      telegram: t('channels.telegram'),
      viber: t('channels.viber'),
    }),
    [t],
  );

  const { isLiveChatAvailable, isOtherChannelsAvailable } = useChannels();

  const handleChannelClick = useCallback(
    (channel: string) => {
      onChannelSelect(channel);
    },
    [onChannelSelect],
  );

  return (
    <div className="sf:w-full sf:h-full sf:max-w-md sf:mx-auto sf:rounded-2xl sf:shadow-lg sf:overflow-hidden sf:background-gradient-diagonal sf:flex sf:flex-col">
      <Header onMinimize={onMinimize} />

      {/* Main Content */}
      <div className="sf:px-6 sf:rounded-full">
        {branding.companyLogoUrl ? (
          <img
            src={branding.companyLogoUrl}
            alt="Company Logo"
            className="sf:w-12 sf:h-12 sf:rounded-full"
          />
        ) : (
          <div className="sf:h-7" />
        )}
      </div>
      <div className="sf:px-6 sf:pb-4 sf:pt-4">
        <h1 className="sf:text-black-10 sf:text-[28px] sf:font-semibold sf:leading-9 sf:mb-1">
          {t(
            'landingPage.startConversationTitle',
            'Always happy to help – just reach out!',
          )}
        </h1>
      </div>

      <div className="sf:rounded-2xl sf-bg-white sf:h-[416px] sf:shadow-[0px_8px_32px_0px_#00000014] sf:mx-6 sf:bg-white sf:overflow-y-auto sf:px-4 sf:py-5">
        {/* LiveChat Section */}
        {isLiveChatAvailable && (
          <LatestMessage onStartChat={onStartChat} />
        )}

        {isLiveChatAvailable && isOtherChannelsAvailable && (
          <div className="sf:my-5 sf:bg-gray-30 sf:h-[1px]" />
        )}

        {/* Channel Selection */}
        {isOtherChannelsAvailable && (
          <div className="sf:space-y-3">
            <h2 className="sf:text-gray-900 sf:text-sm sf:font-semibold">
              {t('landingPage.reachUsTitle')}
            </h2>

            <div className="sf:space-y-2">
              {availableChannels.whatsapp && (
                <ChannelButton
                  platform="whatsapp"
                  label={channelLabels.whatsapp}
                  onClick={() => handleChannelClick('whatsapp')}
                />
              )}

              {availableChannels.facebook && (
                <ChannelButton
                  platform="facebook"
                  label={channelLabels.facebook}
                  onClick={() => handleChannelClick('facebook')}
                />
              )}

              {availableChannels.instagram && (
                <ChannelButton
                  platform="instagram"
                  label={channelLabels.instagram}
                  onClick={() => handleChannelClick('instagram')}
                />
              )}

              {availableChannels.wechat && (
                <ChannelButton
                  platform="wechat"
                  label={channelLabels.wechat}
                  onClick={() => handleChannelClick('wechat')}
                />
              )}

              {availableChannels.line && (
                <ChannelButton
                  platform="line"
                  label={channelLabels.line}
                  onClick={() => handleChannelClick('line')}
                />
              )}

              {availableChannels.telegram && (
                <ChannelButton
                  platform="telegram"
                  label={channelLabels.telegram}
                  onClick={() => handleChannelClick('telegram')}
                />
              )}

              {availableChannels.viber && (
                <ChannelButton
                  platform="viber"
                  label={channelLabels.viber}
                  onClick={() => handleChannelClick('viber')}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="sf:mt-auto">
        <Footer />
      </div>
    </div>
  );
};

export default ChatLandingPage;
